import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { editCheckIn } from '@/api/client';
import type { CheckInDto } from '@/api/types';

/**
 * ---- ⭐⭐ THE LOAD NUMBER THE DRIVER OWED, TYPED AT THE DESK (2026-09-24) ----
 *
 * > **Pete:** *"I clicked the bottom warning and it pulls up a different screen from
 * > the one above it... Either we do a separate entry for the PO, BOL and Reference
 * > number just like the first one (which I think is good) or we bring up the whole
 * > edit screen of the driver."*
 *
 * ⭐ HE CHOSE THE FOCUSED DIALOG, and the reason is the one `VehicleNumberDialog`
 * already states: **an alert that does not open the thing that clears it is a
 * notification, not a task.** Warning 3 landed on three fields; warning 4 landed on a
 * nine-field arrival editor. Two debts on one card behaved differently.
 *   ** TWO ALERTS THAT NAME THE SAME KIND OF DEBT MUST CLEAR THE SAME WAY, OR THE
 *      SCHEDULER LEARNS THE SCREEN TWICE. **
 *
 * ---- ⛔⛔ AND IT REPLACES A PATH THAT COULD ERASE THE CARD -------------------
 *
 * The warning used to call `setEditFor(c)` while every other route called
 * `startEdit(c)` -- and `startEdit` is what fills the draft. So the editor opened
 * BLANK, and `saveEdit` writes every field whose draft differs from the record.
 * Pressing Save on that empty form wrote NULL over the driver name, phone, carrier,
 * PO, BOL, reference, container and trailer.
 *   ** AN EDIT FORM THAT OPENS EMPTY IS NOT A FORM WITH NOTHING IN IT. IT IS A FORM
 *      ABOUT TO ERASE WHAT IS THERE. **
 *   ** AND A SETTER THAT OPENS A DIALOG WITHOUT SEEDING IT IS ONE CALL SITE AWAY
 *      FROM DATA LOSS, EVERY TIME. **
 * ⚠️ That bug is fixed at its own call site too -- this dialog is the destination,
 * not the patch.
 *
 * ---- ⛔ NOTHING HERE DECIDES WHAT IS VALID ---------------------------------
 *
 * ⛔ THE BYPASS CODE IS NOT WRITTEN IN THIS FILE, for the reason its sibling gives:
 * `web/` is a separate tsconfig project and cannot import `src/lib/paperworkNumber.ts`,
 * so a check here would RESTATE the rule. The route refuses and this shows the
 * sentence it sends back.
 *   ** A RULE THE CLIENT KEEPS IS NOT A RULE. IT IS A COURTESY. **
 *
 * ---- ⭐ THE FLAG IS CLEARED BY THE SERVER ----------------------------------
 *
 * `editCheckIn` derives `PaperworkPending` from what the row ends up holding. Filling
 * any one of these clears the alert; blanking the last one puts the chase back on.
 * This dialog posts fields and reloads -- there is no second rule here to fall out of
 * step with that one.
 */
export function PaperworkNumberDialog({
    open, checkIn, getToken, onClose, onSaved,
}: {
    open: boolean;
    /** Null while shut. ⚠️ Read on every render — never copied into state. */
    checkIn: CheckInDto | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [poNumber, setPoNumber] = useState('');
    const [bolNumber, setBolNumber] = useState('');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * ⛔ RE-SEEDED FROM THE ARRIVAL EVERY TIME IT OPENS, KEYED ON THE CHECK-IN ID --
     * the same rule as the vehicle dialog and for the same reason: without the id in
     * the deps the second driver opened inherits the first one's boxes, and these are
     * exactly the fields where a carried-over value looks like a real answer. The rail
     * polls, so it must not re-run on every refresh either.
     */
    useEffect(() => {
        if (!open || !checkIn) return;
        setPoNumber(checkIn.poNumber ?? '');
        setBolNumber(checkIn.bolNumber ?? '');
        setReferenceNumber(checkIn.referenceNumber ?? '');
        setError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, checkIn?.checkInId]);

    if (!checkIn) return null;

    const truck = [
        checkIn.driverName || 'Driver not named',
        checkIn.carrierCode,
        checkIn.appointmentNumber,
    ].filter(Boolean).join(' · ');

    /**
     * ⚠️ SENDS ONLY WHAT CHANGED. The endpoint treats an absent key as "leave it
     * alone", so posting all three every time would write a history line for two
     * fields nobody touched -- and the arrival log is the record a dispute reads.
     */
    async function save() {
        if (!checkIn) return;
        const before: Record<string, string> = {
            poNumber: checkIn.poNumber ?? '',
            bolNumber: checkIn.bolNumber ?? '',
            referenceNumber: checkIn.referenceNumber ?? '',
        };
        const now: Record<string, string> = {
            poNumber: poNumber.trim(),
            bolNumber: bolNumber.trim(),
            referenceNumber: referenceNumber.trim(),
        };
        const changes: Record<string, string | null> = {};
        for (const k of Object.keys(before)) {
            if (now[k] !== before[k]) changes[k] = now[k] === '' ? null : now[k];
        }
        if (Object.keys(changes).length === 0) { onClose(); return; }

        setBusy(true);
        setError(null);
        try {
            await editCheckIn(await getToken(), checkIn.checkInId, changes);
            onSaved();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not save that number.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>PO, BOL or reference</DialogTitle>
                    <DialogDescription>{truck}</DialogDescription>
                </DialogHeader>

                {/**
                  * ⚠️ SHOWN ONLY WHEN HE ACTUALLY OWES ONE -- the dialog also opens to
                  * correct a number that was given. An amber "he owes this" banner on a
                  * truck that answered at the gate is colour spent on nothing (§1AV.269).
                  * ⛔ `=== true`, the tri-state rule: NULL means this site does not collect
                  * paperwork numbers and there is nothing to chase.
                  */}
                {checkIn.paperworkPending === true && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                        This driver typed the bypass code at the gate — he was asked for a PO,
                        BOL or reference number and did not have one. Anything entered here
                        clears the alert on his card.
                    </div>
                )}

                <div className="space-y-2.5">
                    {/* ⭐ SAID ONCE, ABOVE THE FIELDS, rather than three times as "optional"
                        labels that would leave a scheduler wondering which is the real one. */}
                    <div className="text-[11px] text-muted-foreground">
                        Any one of these is enough. The number is what matches this truck to a
                        load and to its paperwork.
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="pn-po">PO number</Label>
                        <Input
                            id="pn-po" value={poNumber} maxLength={50} disabled={busy}
                            autoFocus
                            onChange={(e) => setPoNumber(e.target.value.toUpperCase())}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="pn-bol">BOL number</Label>
                        <Input
                            id="pn-bol" value={bolNumber} maxLength={50} disabled={busy}
                            onChange={(e) => setBolNumber(e.target.value.toUpperCase())}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="pn-ref">Reference number</Label>
                        <Input
                            id="pn-ref" value={referenceNumber} maxLength={50} disabled={busy}
                            onChange={(e) => setReferenceNumber(e.target.value.toUpperCase())}
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                        {error}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button size="sm" disabled={busy} onClick={() => void save()}>
                        {busy ? 'Saving…' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
